import phoneNumbers from 'constants/phoneNumbers';
import { useSelector } from 'react-redux';
import { makeid } from 'utils/helpers/makeid';
import { isPractice, isRoleNameOnboarding } from 'utils/userTypeHelper';
import { Availability } from 'infrastructure/classes/availability';
import { useTranslation } from 'react-i18next';
import ControlButtonLabel from 'components/atoms/control-panel-dropdown-label';
import { isAdminModule, isRpmModule } from 'infrastructure/functions';
import { useAppDispatch, type RootState } from 'store';

import { usePatientContactAvailability } from '../patient/use-patient-contact-availability';
import useAlert from '../utils/use-alert';

import type { ConnectDetails } from 'store/connect';
import type { ModuleType, Roles, UserType } from 'infrastructure/enums';
import type { Nullable } from 'infrastructure/types';

interface IClickConnectProps {
  phoneNumber: string;
  isRpmCall?: boolean;
  doNotCall?: boolean;
  isPatient?: boolean;
}

interface Props {
  patientGuid: string;
  doNotCallReason?: string;
  doNotCall?: boolean;
  doctorPhone?: Nullable<string>;
  patientPhone: string;
  patientHomeNumber?: string;
  emergencyContactNumber?: string;
  module: ModuleType;
  userType: UserType;
  roleNames: Roles[];
  patientTimeZone: string;
  setRpmManualEnteredCallPopupOpen?: (arg: boolean) => void;
  setCcmManualEnteredCallPopupOpen?: (arg: boolean) => void;
}

export const useCallButtons = ({
  patientGuid,
  doNotCall,
  doNotCallReason,
  patientPhone,
  patientHomeNumber,
  emergencyContactNumber,
  module,
  userType,
  roleNames,
  patientTimeZone,
  setRpmManualEnteredCallPopupOpen,
  setCcmManualEnteredCallPopupOpen,
}: Props) => {
  const {
    connect: { handleConnectPopoutOpen, connectDetails },
    linkedCallsReadings: { setIsPopupOpen },
  } = useAppDispatch();
  const canCall = useSelector((state: RootState) => state.connect.canCall);

  const { availabilities } = usePatientContactAvailability({
    patientGuid,
  });
  const { Alert, showAlert } = useAlert();

  const { t } = useTranslation();

  const onClickConnect = async ({
    phoneNumber,
    isRpmCall,
    doNotCall: dnc,
    isPatient = false,
  }: IClickConnectProps) => {
    const isAvailable = Availability.isAvailable(
      availabilities,
      patientTimeZone,
    );

    if (isPatient && !isAvailable) {
      const confirmed = await showAlert({
        title: t('confirm.title'),
        messageTitle: t('confirm.patientIsNotAvailable'),
        type: 'primary',
      });

      if (!confirmed) return;
    }

    if (isPatient && dnc) {
      const confirmed = await showAlert({
        title: t('confirm.title'),
        messageTitle: doNotCallReason ?? t('confirm.patientIsNotAvailable'),
        type: 'primary',
      });

      if (!confirmed) return;
    }

    const details: ConnectDetails = {
      phone: phoneNumber,
      guid: patientGuid,
      isRpmCall,
      readingId: '',
      deviceId: '',
    };
    const showAlertFn = () =>
      showAlert({
        title: t('confirm.title'),
        messageTitle: t('confirm.wouldYouLikeToLinkReadingToCall'),
        type: 'primary',
        rejectBtnLabel: t('controls.no'),
        successBtnLabel: t('controls.yes'),
      });

    const linkReadingsToCall = !isRpmCall ? false : await showAlertFn();

    if (linkReadingsToCall) {
      setIsPopupOpen(true);
      connectDetails(details);
      return; // handleConnectPopoutOpen(true) will be called after select linked readings
    }

    connectDetails(details);
    handleConnectPopoutOpen(true);
  };

  const disabled = !canCall;

  const callButtons = [
    {
      id: makeid(5),
      render: true,
      label: (
        <ControlButtonLabel
          title={t('labels.callPrimary')}
          subtitle={t('labels.phoneNumber')}
        />
      ),
      disabled,
      handleClick: (isRpm?: boolean) => {
        onClickConnect({
          phoneNumber: patientPhone,
          isRpmCall: isRpm ?? isRpmModule(module),
          doNotCall,
          isPatient: true,
        });
      },
    },
    {
      id: makeid(5),
      render: patientHomeNumber,
      label: (
        <ControlButtonLabel
          title={t('labels.callSecondary')}
          subtitle={t('labels.phoneNumber')}
        />
      ),
      disabled,
      handleClick: (isRpm?: boolean) =>
        onClickConnect({
          phoneNumber: patientHomeNumber!,
          isRpmCall: isRpm ?? isRpmModule(module),
          doNotCall,
          isPatient: true,
        }),
    },
    {
      id: makeid(5),
      render: emergencyContactNumber,
      label: (
        <ControlButtonLabel
          title={t('labels.callEmergency')}
          subtitle={t('labels.contact')}
        />
      ),
      disabled,
      handleClick: (isRpm?: boolean) =>
        onClickConnect({
          phoneNumber: emergencyContactNumber!,
          isRpmCall: isRpm ?? isRpmModule(module),
        }),
    },
    {
      id: makeid(5),
      render: !isPractice(userType) || isRoleNameOnboarding(roleNames),
      label: (
        <ControlButtonLabel
          title={t('labels.callTranslation')}
          subtitle={t('labels.service')}
        />
      ),
      disabled,
      handleClick: (isRpm?: boolean) =>
        onClickConnect({
          phoneNumber: phoneNumbers.TRANSLATION_SERVICE_NUMBER,
          isRpmCall: isRpm ?? isRpmModule(module),
        }),
    },
    {
      id: makeid(5),
      render:
        (typeof setRpmManualEnteredCallPopupOpen === 'function' ||
          typeof setCcmManualEnteredCallPopupOpen === 'function') &&
        !isAdminModule(module),
      label: (
        <ControlButtonLabel
          title={t('labels.manualEntered')}
          subtitle={t('labels.call')}
        />
      ),
      disabled: false,
      handleClick: (isRpm?: boolean) => {
        if (isRpm) {
          setRpmManualEnteredCallPopupOpen?.(true);
        } else {
          setCcmManualEnteredCallPopupOpen?.(true);
        }
      },
    },
  ].filter(({ render }) => render);

  return { callButtons, callButtonsAlert: Alert };
};
